import {
  computed,
  EnvironmentInjector,
  inject,
  Injectable,
  InjectionToken,
  isDevMode,
  Provider,
  runInInjectionContext,
  signal,
  Signal,
} from '@angular/core';

import { signalMethod } from '@ngrx/signals';

import { provideRegistry, Registry, RegistryInitializer } from '@kros-sk/core/registry';

import { SchemaQueryConfig, SchemaQueryDeclaration } from '../model/schema-query.types';

export type SortDirection = 'asc' | 'desc';

/**
 * Where a view's sort lives. Everything that touches sorting goes through this — the control that
 * shows it, the sheet that changes it, and the data source that builds the request — so none of them
 * assumes the URL, and swapping the URL for a store changes only which implementation is registered.
 */
export interface SchemaSortState {
  /** The effective sort: the declared default until someone chooses another. `null` means unsorted. */
  readonly field: Signal<string | null>;
  readonly direction: Signal<SortDirection | null>;

  /**
   * Whether the current sort was chosen rather than declared. A control shows itself as active on this
   * and not on a comparison of its own: only the state knows what the default was.
   */
  readonly isExplicit: Signal<boolean>;

  apply(field: string, direction: SortDirection): void;
}

/**
 * What a view is filtered by. Parameterised rather than described: filter *values* are specific to the
 * evidence being filtered, so a shape for them here could only be an untyped bag. What is general is
 * that there are values, that some of them count as active, and that something can replace them.
 */
export interface SchemaFilterState<TValues = unknown> {
  readonly values: Signal<TValues>;

  /** How many filters are applied, for a control that shows a count without understanding the values. */
  readonly activeCount: Signal<number>;

  apply(values: TValues): void;

  /**
   * Back to no filter at all. Separate from `apply` because the one control that needs it — the empty
   * state's "clear all filters" — is generic and cannot construct a `TValues`. Only the implementation
   * knows what an unfiltered view is, and it need not be what an application calls its initial filter.
   */
  clear(): void;
}

/** The full-text term a view is narrowed by. */
export interface SchemaSearchState {
  readonly value: Signal<string>;

  apply(value: string): void;
}

/**
 * How a query state is registered: a function, not a class. One is a handful of signals over a store, so
 * it needs neither an injector of its own nor a lazily loaded chunk — which is what `ServicesRegistry`
 * buys and what would cost a resolve that lands turns later. It runs in an injection context, so it
 * may `inject()` whatever its storage needs.
 */
export type SchemaQueryStateFactory<TState, TConfig = unknown> = (config: TConfig | undefined) => TState;

export type SchemaSortStateFactory<TConfig = unknown> = SchemaQueryStateFactory<SchemaSortState, TConfig>;
export type SchemaFilterStateFactory<TValues = unknown, TConfig = unknown> = SchemaQueryStateFactory<
  SchemaFilterState<TValues>,
  TConfig
>;
export type SchemaSearchStateFactory<TConfig = unknown> = SchemaQueryStateFactory<SchemaSearchState, TConfig>;

/**
 * One registry per state rather than one for all three: the value types differ, and the keys would
 * otherwise collide — `url` means a different thing for each.
 *
 * Each holds factories whose config is `never`, which leaves `undefined` as all a registered factory has to
 * accept. An implementation types the config it reads — `urlSortState` takes a `UrlSortStateConfig` — and
 * under `strictFunctionTypes` no such factory is a function of `unknown`, so registries typed that way took one
 * only in a project that is not strict. The config stays opaque either way: `SchemaQueryHost` hands it over.
 */
export const SCHEMA_SORT_STATES_REGISTRY = new InjectionToken<Registry<SchemaSortStateFactory<never>>>('SCHEMA_SORT_STATES_REGISTRY');
export const SCHEMA_FILTER_STATES_REGISTRY = new InjectionToken<Registry<SchemaFilterStateFactory<unknown, never>>>(
  'SCHEMA_FILTER_STATES_REGISTRY'
);
export const SCHEMA_SEARCH_STATES_REGISTRY = new InjectionToken<Registry<SchemaSearchStateFactory<never>>>('SCHEMA_SEARCH_STATES_REGISTRY');

/**
 * Register the query state implementations reachable from this injector. All three ship empty, the way the
 * actions registry does: the renderer defines the concept and an application says what backs it.
 */
export function provideSchemaSortStates(...entries: RegistryInitializer<SchemaSortStateFactory<never>>[]): Provider {
  return provideRegistry(SCHEMA_SORT_STATES_REGISTRY, ...entries);
}

export function provideSchemaFilterStates(...entries: RegistryInitializer<SchemaFilterStateFactory<unknown, never>>[]): Provider {
  return provideRegistry(SCHEMA_FILTER_STATES_REGISTRY, ...entries);
}

export function provideSchemaSearchStates(...entries: RegistryInitializer<SchemaSearchStateFactory<never>>[]): Provider {
  return provideRegistry(SCHEMA_SEARCH_STATES_REGISTRY, ...entries);
}

/**
 * Carries the resolved query states down the tree. Every schema node hosts one, so most of them
 * declare nothing; a host that answered with only its own states would cut its subtree off from the
 * ones declared above it.
 *
 * States are resolved when the declaration arrives rather than when one is first read, so a factory
 * runs in a plain injection context and may do what a factory normally does — subscribe, inject, read
 * a store. The context is the nearest environment injector, so anything a state registers for cleanup
 * lives as long as the route rather than as long as the node.
 */
@Injectable()
export class SchemaQueryHost {
  readonly #parent = inject(SchemaQueryHost, { skipSelf: true, optional: true });
  readonly #injector = inject(EnvironmentInjector);

  readonly #sortRegistry = inject(SCHEMA_SORT_STATES_REGISTRY, { optional: true });
  readonly #filterRegistry = inject(SCHEMA_FILTER_STATES_REGISTRY, { optional: true });
  readonly #searchRegistry = inject(SCHEMA_SEARCH_STATES_REGISTRY, { optional: true });

  readonly #sort = signal<SchemaSortState | null>(null);
  readonly #filter = signal<SchemaFilterState | null>(null);
  readonly #search = signal<SchemaSearchState | null>(null);

  /**
   * Nearest declaration wins, one state at a time — which is what lets a node declare `filter` and go
   * on inheriting `sort`.
   *
   * Annotated because the chain to the parent host makes the type self-referential, which TypeScript
   * cannot infer.
   */
  readonly sort: Signal<SchemaSortState | null> = computed(() => this.#sort() ?? this.#parent?.sort() ?? null);
  readonly filter: Signal<SchemaFilterState | null> = computed(() => this.#filter() ?? this.#parent?.filter() ?? null);
  readonly search: Signal<SchemaSearchState | null> = computed(() => this.#search() ?? this.#parent?.search() ?? null);

  readonly registerQuery = signalMethod<SchemaQueryConfig | undefined>((query) => {
    this.#sort.set(this.#resolve('sort', query?.sort, this.#sortRegistry));
    this.#filter.set(this.#resolve('filter', query?.filter, this.#filterRegistry));
    this.#search.set(this.#resolve('search', query?.search, this.#searchRegistry));
  });

  #resolve<TState>(
    stateName: string,
    declaration: SchemaQueryDeclaration | undefined,
    registry: Registry<SchemaQueryStateFactory<TState, never>> | null
  ): TState | null {
    if (!declaration) {
      return null;
    }

    if (!registry) {
      reportMissingRegistry(stateName, declaration.type);

      return null;
    }

    /**
     * `getEntry` throws for an unregistered key. An unknown state type is a schema-authoring bug an end
     * user can do nothing about, so it is reported the way an unresolvable `componentType` is: named in
     * dev mode, silent in production, and the state simply absent either way.
     */
    let factory: SchemaQueryStateFactory<TState, never>;

    try {
      factory = registry.getEntry(declaration.type)();
    } catch (failure) {
      reportResolveFailure(stateName, declaration.type, failure);

      return null;
    }

    /**
     * The one place the declaration's config meets the factory, hence the one cast: the registries type it
     * `never` so every implementation can type its own, and the renderer hands it over without reading it.
     */
    const createState = factory as SchemaQueryStateFactory<TState>;

    return runInInjectionContext(this.#injector, () => createState(declaration.config));
  }
}

const NOT_DECLARED = signal(null).asReadonly();

/**
 * How anything reaches the query states declared for its node — a sort control, a table header, a
 * dashboard period picker, or the data source building the request. Each returns `null` where the
 * state is not declared on this node or above it, which is the ordinary case rather than an error:
 * a view that does not sort has no sort state.
 *
 * The host itself is optional for the same reason. A data source is routinely constructed outside any
 * schema node — every spec that builds one directly does exactly that — and "no host above me" is the
 * same answer as "nothing declared above me". Requiring it would turn the ordinary case into an
 * NG0201 naming a class the caller never asked for.
 */
export function injectSchemaSortState(): Signal<SchemaSortState | null> {
  return inject(SchemaQueryHost, { optional: true })?.sort ?? NOT_DECLARED;
}

export function injectSchemaFilterState<TValues = unknown>(): Signal<SchemaFilterState<TValues> | null> {
  return (inject(SchemaQueryHost, { optional: true })?.filter ?? NOT_DECLARED) as Signal<SchemaFilterState<TValues> | null>;
}

export function injectSchemaSearchState(): Signal<SchemaSearchState | null> {
  return inject(SchemaQueryHost, { optional: true })?.search ?? NOT_DECLARED;
}

function reportMissingRegistry(stateName: string, type: string): void {
  if (isDevMode()) {
    console.warn(
      `Schema node declares ${stateName} "${type}", but no registry for it is reachable from this injector, ` +
        `so the state stays unset. Add provideSchema${capitalize(stateName)}States(…) where this schema is rendered.`
    );
  }
}

function reportResolveFailure(stateName: string, type: string, failure: unknown): void {
  if (isDevMode()) {
    console.warn(
      `Schema ${stateName} "${type}" could not be resolved, so the state stays unset. ` +
        `Register it with addEntryToRegistry("${type}", …) in an injector this node can reach.`,
      failure
    );
  }
}

function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}
